import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';
import OtpInput from '../components/OtpInput';
import TradeErrorDialog from '../components/TradeErrorDialog';
import tradeError from '../utils/tradeError';
import useSmsOtp from '../hooks/useSmsOtp';
import { normalizeOtp, otpDigits } from '../utils/otp';
import { Link, useNavigate } from 'react-router-dom';
import './Trade.css';
import { ArrowUpCircle, ArrowDownCircle, Banknote, Coins, Scale, AlertCircle, CheckCircle, ArrowRight, ArrowUpRight, ArrowRightLeft, ChevronLeft, Clock, Wallet, ShieldCheck, RefreshCw, MessageCircle, X } from '../components/icons';

export default function Trade() {
  const [isHolidayToday, setIsHolidayToday] = useState(false);
const [isCheckingHoliday, setIsCheckingHoliday] = useState(true);
  const [priceData, setPriceData] = useState(null);
  const [dataUnavailable, setDataUnavailable] = useState(false);
  const [requestSummary, setRequestSummary] = useState(null);
  const [tradeFailure, setTradeFailure] = useState(null);
  const dismissTradeFailure = useCallback(() => setTradeFailure(null), []);
  const operationBusy = useRef(false);
  const dataBusy = useRef(false);
  const availabilityNotice = useRef(null);
  const dialogRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [balances, setBalances] = useState({ balance_rial: null, balance_gold: null });
  const [amountRial, setAmountRial] = useState('');
  const [amountGold, setAmountGold] = useState('');
  const [activeTab, setActiveTab] = useState('buy');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [code, setCode] = useState(['', '', '', '', '']);
  const { start: startSmsOtp, cancel: cancelSmsOtp } = useSmsOtp(value => setCode(otpDigits(value)));
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');
  const [pendingGoldRequests, setPendingGoldRequests] = useState([]);
  const [pendingWithdrawRequests, setPendingWithdrawRequests] = useState([]);

  // --- توابع کمکی ---
  const vibrate = (pattern = 50) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  };

  const numberToPersianWords = (number) => {
    const units = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'];
    const teens = ['ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نوزده'];
    const tens = ['', '', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'];
    const hundreds = ['', 'یکصد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد'];
    const thousands = ['هزار', 'میلیون', 'میلیارد'];

    if (number === 0) return 'صفر';

    const toWords = (num) => {
      if (num < 10) return units[num];
      if (num < 20) return teens[num - 10];
      if (num < 100) {
        const ten = Math.floor(num / 10);
        const unit = num % 10;
        return unit === 0 ? tens[ten] : `${tens[ten]} و ${units[unit]}`;
      }
      if (num < 1000) {
        const hundred = Math.floor(num / 100);
        const rest = num % 100;
        return rest === 0 ? hundreds[hundred] : `${hundreds[hundred]} و ${toWords(rest)}`;
      }
      return num.toString();
    };

    let result = '';
    let num = Math.floor(number);
    let level = 0;

    while (num > 0) {
      const part = num % 1000;
      if (part > 0) {
        const partWords = toWords(part);
        const thousand = level > 0 ? ` ${thousands[level - 1]}` : '';
        result = `${partWords}${thousand}${result ? ' و ' + result : ''}`;
      }
      num = Math.floor(num / 1000);
      level++;
    }

    return result || 'صفر';
  };

  const convertToSootWords = (goldAmount) => {
    if (!goldAmount || isNaN(goldAmount)) return '';
    const kilograms = Math.floor(goldAmount / 1000);
    const grams = Math.floor(goldAmount % 1000);
    const soot = Math.round((goldAmount - Math.floor(goldAmount)) * 1000);
    let result = '';

    if (kilograms > 0) result += numberToPersianWords(kilograms) + ' کیلوگرم';
    if (grams > 0) result += (result ? ' و ' : '') + numberToPersianWords(grams) + ' گرم';
    if (soot > 0) result += (result ? ' و ' : '') + numberToPersianWords(soot) + ' سوت';
    return result || 'صفر';
  };

  const persianToEnglishDigits = (str) => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    let result = str.toString();
    for (let i = 0; i < persianDigits.length; i++) {
      result = result.replace(new RegExp(persianDigits[i], 'g'), englishDigits[i]);
    }
    return result.replace(/[\u0660-\u0669]/g, digit => String(digit.charCodeAt(0) - 0x660)).replace(/\u066b/g, '.');
  };

  // --- فرمت اعداد (امن) ---
  const formatNumber = (value) => {
    if (value == null || isNaN(parseFloat(value))) return '';
    const num = parseFloat(value);
    return num.toLocaleString('fa-IR');
  };

  const formatGoldNumber = (value) => {
    if (value == null || isNaN(parseFloat(value))) return '';
    const num = parseFloat(value);
    if (num === 0) return '0.000';
    return num.toLocaleString('fa-IR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    });
  };

  // --- محاسبات ---
  const calculatedGoldFromRial = amountRial && priceData?.buy_price && priceData?.sell_price ? (
    activeTab === 'buy'
      ? parseFloat(amountRial) / priceData.buy_price 
      : parseFloat(amountRial) / priceData.sell_price
  ) : 0;

  const calculatedRialFromGold = amountGold && priceData?.buy_price && priceData?.sell_price ? (
    activeTab === 'buy'
      ? parseFloat(amountGold) * priceData.buy_price
      : parseFloat(amountGold) * priceData.sell_price
  ) : 0;

  // --- هندلرها ---
  const handleAmountRialChange = (e) => {
    let raw = e.target.value;
    raw = persianToEnglishDigits(raw);
    raw = raw.replace(/[^\d]/g, '');
    setAmountRial(raw);
    setAmountGold('');
  };

  const handleAmountGoldChange = (e) => {
    let raw = e.target.value;
    raw = persianToEnglishDigits(raw);
    raw = raw.replace(/[^\d.]/g, '');
    const parts = raw.split('.');
    if (parts.length > 2) return;
    if (parts[1] && parts[1].length > 6) return;
    setAmountGold(raw);
    setAmountRial('');
  };

  // --- توابع API ---
  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const res = await api.post('/api/auth/refresh-token', { refreshToken });
      localStorage.setItem('authToken', res.data.accessToken);
      return res.data.accessToken;
    } catch (err) {
      if (err.response?.status !== 401 && localStorage.getItem('refreshToken')) throw err;
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      navigate('/login_page');
      return null;
    }
  };

  const fetchData = async () => {
    if (dataBusy.current) return;
    dataBusy.current = true;
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');

      const [pricesRes, balancesRes, goldRequestsRes, withdrawRequestsRes] = await Promise.all([
        api.get('/api/prices/my-prices', { headers: { Authorization: `Bearer ${newToken}` }, timeout: 10000 }),
        api.get('/api/wallet/balances', { headers: { Authorization: `Bearer ${newToken}` }, timeout: 10000 }),
        api.get('/api/gold/user-requests', { headers: { Authorization: `Bearer ${newToken}` }, timeout: 10000 }),
        api.get('/api/wallet/requests/pending', { headers: { Authorization: `Bearer ${newToken}` }, timeout: 10000 })
      ]);

      // دیباگ: ببین چی دریافت می‌شه
      // console.log('قیمت‌های دریافتی:', pricesRes.data);

      setPriceData(pricesRes.data);
      setDataUnavailable(false);

      setBalances({
        balance_rial: parseFloat(balancesRes.data.balance_rial || 0),
        balance_gold: parseFloat(balancesRes.data.balance_gold || 0)
      });

      const goldRequests = Array.isArray(goldRequestsRes.data) ? goldRequestsRes.data : [];
      setPendingGoldRequests(goldRequests.filter(req => req?.status === 'pending'));

      const withdrawRequests = Array.isArray(withdrawRequestsRes.data) ? withdrawRequestsRes.data : [];
      setPendingWithdrawRequests(withdrawRequests);

    } catch (err) {
      setDataUnavailable(true);
      console.error('خطا در دریافت اطلاعات:', err);
      setError(err.response?.data?.message || 'خطا در دریافت اطلاعات');
      setTimeout(() => setError(''), 3000);
    } finally {
      dataBusy.current = false;
    }
  };

// داخل useEffect اصلی (همراه fetchData)
useEffect(() => {
  if (!token) {
    navigate('/login_page');
    return;
  }

  const loadData = async () => {
    setIsCheckingHoliday(true);
    try {
      await Promise.all([
        fetchData(),
        checkHolidayStatus()
      ]);
    } finally {
      setIsCheckingHoliday(false);
    }
  };

  loadData();
  const interval = setInterval(fetchData, 10000);
  return () => clearInterval(interval);
}, [token, navigate]);

// تابع جدید: بررسی وضعیت تعطیلات
const checkHolidayStatus = async () => {
  try {
    const res = await api.get('/api/prices/holiday-status');
    setIsHolidayToday(res.data.is_holiday);
  } catch (err) {
    console.warn('خطا در دریافت وضعیت تعطیلات:', err);
    setIsHolidayToday(false);
  }
};

  useEffect(() => {
    let interval;
    if (isCodeSent && timer > 0) {
      interval = setInterval(() => setTimer(prev => prev - 1), 1000);
    } else if (timer <= 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [isCodeSent, timer]);

  const showMessage = (msg, type) => {
    if (type === 'success') setSuccess(msg);
    else setTradeFailure(tradeError(msg, { type: requestSummary?.type || activeTab }));
    if (type === 'success') setTimeout(() => setSuccess(''), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (operationBusy.current) return;
    vibrate(50);

    if ((!amountRial && !amountGold) || !Number.isFinite(Number(amountRial || amountGold)) || Number(amountRial || amountGold) <= 0) return showMessage('لطفاً مقدار را وارد کنید', 'error');
    if (!canTrade) return showMessage('قیمت‌ها قدیمی هستند', 'error');

    operationBusy.current = true;
    setIsLoading(true);
    const summary = { type: activeTab, input: amountRial ? 'rial' : 'gold', entered: Number(amountRial || amountGold), gold: Number(amountGold || calculatedGoldFromRial), rial: Number(amountRial || calculatedRialFromGold) };

    setCode(otpDigits(''));
    startSmsOtp();
    try {
      const newToken = token || (await refreshAccessToken());
      const payload = amountRial ? { amount_rial: parseFloat(amountRial) } : { amount_gold: parseFloat(amountGold) };
      const endpoint = activeTab === 'buy' ? '/api/prices/buy' : '/api/prices/sell';
      const res = await api.post(endpoint, { ...payload, otp_host: window.location.hostname }, { headers: { Authorization: `Bearer ${newToken}` }, timeout: 10000 });

      showMessage(res.data.message, 'success');
      setRequestSummary(summary);
      setIsCodeSent(true);
      setTimer(60);
      setCanResend(false);
      vibrate([100, 50, 100]);
    } catch (err) {
      cancelSmsOtp();
      vibrate(200);
      setTradeFailure(tradeError(err, { stage: 'request', type: activeTab }));
    } finally {
      operationBusy.current = false;
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (operationBusy.current || !canResend) return;
    operationBusy.current = true;
    setIsLoading(true);
    setCode(otpDigits(''));
    startSmsOtp();
    try {
      const newToken = token || (await refreshAccessToken());
      const payload = amountRial ? { amount_rial: parseFloat(amountRial) } : { amount_gold: parseFloat(amountGold) };
      const endpoint = activeTab === 'buy' ? '/api/prices/buy' : '/api/prices/sell';
      await api.post(endpoint, { ...payload, otp_host: window.location.hostname }, { headers: { Authorization: `Bearer ${newToken}` }, timeout: 10000 });
      setRequestSummary({ type: activeTab, input: amountRial ? 'rial' : 'gold', entered: Number(amountRial || amountGold), gold: Number(amountGold || calculatedGoldFromRial), rial: Number(amountRial || calculatedRialFromGold) });
      setTimer(60);
      setCanResend(false);
      showMessage('کد جدید ارسال شد', 'success');
    } catch (err) {
      cancelSmsOtp();
      setTradeFailure(tradeError(err, { stage: 'resend', type: requestSummary?.type || activeTab }));
    } finally {
      operationBusy.current = false;
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e, suppliedCode) => {
    e.preventDefault();
    if (operationBusy.current) return;
    vibrate(50);
    const verificationCode = normalizeOtp(suppliedCode || code.join(''));
    if (verificationCode.length !== 5) return showMessage('کد ۵ رقمی را کامل وارد کنید', 'error');
    cancelSmsOtp();
    operationBusy.current = true;
    setIsLoading(true);

    try {
      const newToken = token || (await refreshAccessToken());
      await api.post('/api/prices/verify-transaction', {
        verification_code: verificationCode,
        transaction_type: requestSummary?.type || activeTab
      }, { headers: { Authorization: `Bearer ${newToken}` }, timeout: 10000 });

      vibrate([100, 50, 100]);
      showMessage('تراکنش موفق!', 'success');
      setAmountRial(''); setAmountGold(''); setIsCodeSent(false); setCode(['', '', '', '', '']);
      fetchData();
    } catch (err) {
      vibrate(200);
      setTradeFailure(tradeError(err, { stage: 'verify', type: requestSummary?.type || activeTab }));
    } finally {
      operationBusy.current = false;
      setIsLoading(false);
    }
  };



  useEffect(() => {
    if (!isCodeSent || tradeFailure) return undefined;
    const previousFocus = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector('input')?.focus();
    const onKey = event => {
      if (event.key === 'Escape') { event.preventDefault(); closeVerification(); }
      if (event.key !== 'Tab') return;
      const nodes = [...(dialogRef.current?.querySelectorAll('button:not(:disabled),input:not(:disabled),a[href]') || [])];
      if (!nodes.length) { event.preventDefault(); return; }
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = overflow; previousFocus?.focus(); };
  }, [isCodeSent, tradeFailure]);

  const chosenPrice = Number(activeTab === 'buy' ? priceData?.buy_price : priceData?.sell_price);
  const estimateGold = Number(amountGold || calculatedGoldFromRial);
  const estimateRial = Number(amountRial || calculatedRialFromGold);
  const validAmount = Number(amountRial || amountGold) > 0 && Number.isFinite(Number(amountRial || amountGold));
  const canTrade = Boolean(priceData?.is_price_fresh) && !dataUnavailable && !isHolidayToday && !isCheckingHoliday && Number.isFinite(chosenPrice) && chosenPrice > 0;
  const showNumber = (value, digits = 0) => value != null && value !== '' && Number.isFinite(Number(value)) ? Number(value).toLocaleString('fa-IR', { maximumFractionDigits: digits }) : '—';
  const quoteTime = priceData?.last_updated ? new Date(priceData.last_updated) : null;
  const quoteDate = quoteTime && Number.isFinite(quoteTime.getTime()) ? new Intl.DateTimeFormat('fa-IR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tehran' }).format(quoteTime) : 'زمان ثبت قیمت مشخص نیست';
  const availabilityIssue = isHolidayToday ? 'holiday' : dataUnavailable ? 'unavailable' : priceData && !priceData.is_price_fresh ? 'stale' : null;
  useEffect(() => {
    if (!availabilityIssue) {
      availabilityNotice.current = null;
      if (tradeFailure?.source === 'availability') setTradeFailure(null);
      return;
    }
    if (tradeFailure || availabilityNotice.current === availabilityIssue) return;
    availabilityNotice.current = availabilityIssue;
    const failure = availabilityIssue === 'unavailable'
      ? { title: 'در انتظار ارتباط با سرور', message: 'اطلاعات معامله از سرور دریافت نمی‌شود. لطفاً منتظر بمانید؛ خرید و فروش پس از دریافت اطلاعات و قیمت معتبر خودکار فعال می‌شود.', guidance: '', compact: true, waiting: true }
      : tradeError(availabilityIssue === 'holiday' ? 'امروز تعطیل رسمی است؛ امکان خرید و فروش وجود ندارد.' : 'قیمت‌ها قدیمی هستند؛ تا دریافت قیمت تازه امکان ثبت درخواست وجود ندارد.');
    setTradeFailure({ ...failure, source: 'availability' });
  }, [availabilityIssue, tradeFailure]);
  const changeTrade = type => { if (isLoading || isCodeSent) return; cancelSmsOtp(); setCode(otpDigits('')); setActiveTab(type); setError(''); setSuccess(''); };
  const closeVerification = () => {
    if (operationBusy.current) return;
    cancelSmsOtp(); setIsCodeSent(false); setCode(otpDigits('')); setTimer(60); setCanResend(false); setRequestSummary(null);
  };

  return <div className="trade-redesign" dir="rtl">
    <Link className="trade-back" to="/"><ArrowRight size={16} /> بازگشت به خانه</Link>
    <section className="trade-hero"><div><span>ESF GOLD / TRADE</span><h1>خرید و فروش طلا،<br /><em>شفاف در هر قدم.</em></h1><p>مبلغ یا وزن را وارد کنید، برآورد را ببینید و با کد پیامکی تأیید کنید.</p></div><div className="trade-hero__symbol" aria-hidden="true"><ArrowRightLeft size={43} /></div><Link to="/transactions_page">سوابق معاملات <ArrowUpRight size={16} /></Link></section>
    <section className="trade-market" aria-label="قیمت خرید و فروش حساب شما"><div className="trade-market__heading"><div><span className="trade-label">قیمت‌های حساب شما</span><h2>نرخ هر گرم طلا</h2></div><div className={`trade-market__status${!priceData?.is_price_fresh || dataUnavailable ? ' is-muted' : ''}`}><span />{dataUnavailable ? 'اطلاعات در دسترس نیست' : !priceData ? 'در حال دریافت قیمت' : priceData.is_price_fresh ? 'قیمت به‌روز' : 'قیمت قدیمی'}<time>{quoteDate}</time></div></div><div className="trade-quotes">{[{ id: 'buy', label: 'قیمت خرید شما', icon: ArrowDownCircle, value: priceData?.buy_price }, { id: 'sell', label: 'قیمت فروش شما', icon: ArrowUpCircle, value: priceData?.sell_price }].map(item => <article key={item.id} className={`trade-quote trade-quote--${item.id}${activeTab === item.id ? ' is-chosen' : ''}`}><span className="trade-quote__icon"><item.icon size={22} /></span><span>{item.label}</span><strong>{showNumber(item.value)} <small>ریال</small></strong><p>{item.value != null ? `${showNumber(Number(item.value) / 10)} تومان` : '—'}</p></article>)}</div></section>
    <div className="trade-grid">
      <section className="trade-workspace" aria-labelledby="trade-form-title"><div className="trade-tabs" role="tablist" aria-label="نوع معامله">{[{ id: 'buy', label: 'خرید طلا', icon: ArrowDownCircle }, { id: 'sell', label: 'فروش طلا', icon: ArrowUpCircle }].map(tab => <button type="button" role="tab" id={`trade-${tab.id}-tab`} aria-selected={activeTab === tab.id} aria-controls="trade-form-panel" key={tab.id} className={activeTab === tab.id ? `is-active is-${tab.id}` : ''} disabled={isLoading || isCodeSent} onClick={() => changeTrade(tab.id)}><tab.icon size={20} />{tab.label}</button>)}</div>
        <div className="trade-panel" id="trade-form-panel" role="tabpanel" aria-labelledby={`trade-${activeTab}-tab`}><div className="trade-form-heading"><span><Scale size={23} /></span><div><h2 id="trade-form-title">{activeTab === 'buy' ? 'چقدر طلا می‌خواهید بخرید؟' : 'چقدر طلا می‌خواهید بفروشید؟'}</h2><p>یکی از دو مقدار مبلغ یا وزن را وارد کنید.</p></div></div>
          {!isCodeSent && error && <div className="trade-message trade-message--error" role="alert"><AlertCircle size={18} /><span>{error}</span></div>}{!isCodeSent && success && <div className="trade-message trade-message--success" role="status"><CheckCircle size={18} /><span>{success}</span></div>}
          <form onSubmit={handleSubmit} aria-busy={isLoading}><label className="trade-field-label" htmlFor="trade-rial">مبلغ معامله <span>ریال</span></label><div className="trade-input"><input id="trade-rial" type="text" inputMode="numeric" dir="ltr" placeholder="۰" value={amountRial ? formatNumber(amountRial) : ''} onChange={handleAmountRialChange} disabled={!canTrade || isLoading || isCodeSent} aria-describedby="trade-rial-help" /><span>ریال</span></div><p className="trade-input-help" id="trade-rial-help">{amountRial ? `معادل ${numberToPersianWords(Math.floor(Number(amountRial) / 10))} تومان` : 'مبلغ را به ریال وارد کنید؛ معادل تومان اینجا نمایش داده می‌شود.'}</p><div className="trade-divider"><span>یا بر اساس وزن</span></div><label className="trade-field-label" htmlFor="trade-gold">وزن طلا <span>گرم</span></label><div className="trade-input"><input id="trade-gold" type="text" inputMode="decimal" dir="ltr" placeholder="۰٫۰۰۰" value={amountGold} onChange={handleAmountGoldChange} disabled={!canTrade || isLoading || isCodeSent} aria-describedby="trade-gold-help" /><span>گرم</span></div><p className="trade-input-help" id="trade-gold-help">{amountGold ? convertToSootWords(Number(amountGold)) : 'با وارد کردن وزن، مبلغ تقریبی معامله محاسبه می‌شود.'}</p>
            <section className="trade-estimate" aria-label="برآورد معامله"><div><span>برآورد {activeTab === 'buy' ? 'خرید' : 'فروش'} شما</span><small>پیش از درخواست</small></div><dl><div><dt>وزن تقریبی</dt><dd>{validAmount ? showNumber(estimateGold, 3) : '—'} <small>گرم</small></dd></div><div><dt>{activeTab === 'buy' ? 'مبلغ پرداختی تقریبی' : 'مبلغ دریافتی تقریبی'}</dt><dd>{validAmount ? showNumber(estimateRial) : '—'} <small>ریال</small></dd></div></dl><p>این مقادیر برآورد هستند؛ محاسبه نهایی هنگام ثبت درخواست در سرور انجام می‌شود.</p></section>
            <button className={`trade-submit trade-submit--${activeTab}`} type="submit" disabled={isLoading || isCodeSent || !canTrade || !validAmount}>{isLoading ? <RefreshCw size={19} className="trade-spinning" /> : <ArrowUpRight size={19} />}{isLoading ? 'در حال ارسال درخواست…' : 'ادامه و دریافت کد تأیید'}</button><p className="trade-submit-note"><ShieldCheck size={14} /> معامله پس از وارد کردن کد پیامکی و تأیید نهایی انجام می‌شود.</p>
          </form>
        </div>
      </section>
      <aside className="trade-sidebar"><section className="trade-account"><div className="trade-side-heading"><Wallet size={21} /><h2>حساب شما</h2><Link to="/deposit-withdraw_page">مدیریت <ChevronLeft size={14} /></Link></div><div className="trade-account__row"><span><Banknote size={17} /> موجودی ریالی</span><strong>{showNumber(balances.balance_rial)} <small>ریال</small></strong><small>{balances.balance_rial != null ? `${showNumber(Number(balances.balance_rial) / 10)} تومان` : '—'}</small></div><div className="trade-account__row"><span><Coins size={17} /> موجودی طلا</span><strong>{showNumber(balances.balance_gold, 3)} <small>گرم</small></strong></div><p>موجودی حساب نمایش داده شده است؛ درخواست‌های در انتظار می‌توانند مبلغ یا طلای قابل معامله را کاهش دهند.</p><Link className="trade-text-link" to="/deposit-withdraw_page">واریز و برداشت <ArrowUpRight size={16} /></Link></section>
        <section className="trade-guide"><span><ShieldCheck size={24} /></span><h2>از انتخاب تا تأیید</h2><ol><li>خرید یا فروش را انتخاب کنید.</li><li>مبلغ یا وزن دلخواه را وارد کنید.</li><li>درخواست را با کد پیامکی تأیید کنید.</li></ol>{priceData?.min_weight != null && priceData?.max_weight != null && <p>بازه مجاز معامله: {showNumber(priceData.min_weight, 6)} تا {showNumber(priceData.max_weight, 6)} گرم</p>}<Link to="/price-chart">مشاهده نمودار قیمت <ArrowUpRight size={16} /></Link></section>
        {(pendingGoldRequests.length > 0 || pendingWithdrawRequests.length > 0) && <section className="trade-pending"><h2><Clock size={18} /> درخواست‌های در انتظار</h2>{pendingWithdrawRequests.length > 0 && <Link to="/wallet-requests_page">برداشت ریالی <b>{pendingWithdrawRequests.length.toLocaleString('fa-IR')}</b><ChevronLeft size={15} /></Link>}{pendingGoldRequests.length > 0 && <Link to="/gold-request_page">دریافت طلا <b>{pendingGoldRequests.length.toLocaleString('fa-IR')}</b><ChevronLeft size={15} /></Link>}</section>}
        <Link className="trade-support" to="/chat_page"><MessageCircle size={21} /><span><strong>برای معامله راهنمایی می‌خواهید؟</strong><small>گفت‌وگو با پشتیبانی</small></span><ChevronLeft size={16} /></Link>
      </aside>
    </div>
    {isCodeSent && !tradeFailure && <div className="trade-dialog-overlay" onClick={event => { if (event.target === event.currentTarget) closeVerification(); }}><section className="trade-dialog" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="trade-confirm-title" dir="rtl"><div className="trade-dialog__header"><span><ShieldCheck size={24} /></span><button type="button" aria-label="بستن تأیید معامله" disabled={isLoading} onClick={closeVerification}><X size={20} /></button></div><span className="trade-label">یک قدم تا ثبت معامله</span><h2 id="trade-confirm-title">تأیید {requestSummary?.type === 'sell' ? 'فروش' : 'خرید'} طلا</h2><p className="trade-dialog__description">کد ۵ رقمی پیامک‌شده را وارد کنید.</p><div className="trade-confirm-summary"><span>خلاصه درخواست</span><dl><div><dt>{requestSummary?.input === 'rial' ? 'مبلغ درخواست' : 'وزن درخواست'}</dt><dd>{showNumber(requestSummary?.entered, requestSummary?.input === 'gold' ? 6 : 0)} {requestSummary?.input === 'gold' ? 'گرم' : 'ریال'}</dd></div><div><dt>{requestSummary?.input === 'rial' ? 'وزن برآوردی' : 'مبلغ برآوردی'}</dt><dd>{showNumber(requestSummary?.input === 'rial' ? requestSummary?.gold : requestSummary?.rial, requestSummary?.input === 'rial' ? 3 : 0)} {requestSummary?.input === 'rial' ? 'گرم' : 'ریال'}</dd></div></dl><small>برآورد مربوط به زمان ارسال درخواست است؛ محاسبه نهایی در سرور انجام می‌شود.</small></div>{error && <div className="trade-message trade-message--error" role="alert"><AlertCircle size={18} /><span>{error}</span></div>}<form onSubmit={handleVerifyCode}><OtpInput value={code.join('')} onChange={value => setCode(otpDigits(value))} disabled={isLoading} /><div className="trade-resend">{canResend ? <button type="button" disabled={isLoading} onClick={handleResendCode}>ارسال دوباره کد</button> : <span>{Math.max(0, timer).toLocaleString('fa-IR')} ثانیه تا ارسال دوباره</span>}</div><button className="trade-submit" type="submit" disabled={isLoading || normalizeOtp(code.join('')).length !== 5}>{isLoading ? 'در حال تأیید…' : 'تأیید و نهایی کردن معامله'}</button><button className="trade-dialog__cancel" type="button" disabled={isLoading} onClick={closeVerification}>بازگشت و ویرایش درخواست</button></form></section></div>}
    {tradeFailure && <TradeErrorDialog failure={tradeFailure} onClose={dismissTradeFailure} />}
  </div>;
}
